import { useEffect, useRef, type ReactNode } from 'react'
import { Button, Dialog } from '@cloudflare/kumo'
import { XIcon } from '@phosphor-icons/react'
import { useTranslation } from '../../i18n'

export interface HomeSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: ReactNode
  children: ReactNode
}

/**
 * Kumo `Dialog` (Base UI: focus trap, Esc to close, `aria-labelledby` from the
 * title) styled as a bottom sheet below 40rem and a centred dialog above.
 * Opened programmatically (no `Dialog.Trigger`), so it remembers the element
 * that had focus when it opened and puts focus back there if closing left
 * focus nowhere.
 */
export function HomeSheet({ open, onOpenChange, title, children }: HomeSheetProps) {
  const { t } = useTranslation()
  const returnTo = useRef<HTMLElement | null>(null)
  const wasOpen = useRef(false)

  if (open && !wasOpen.current && typeof document !== 'undefined') {
    returnTo.current = document.activeElement as HTMLElement | null
  }

  useEffect(() => {
    const closing = wasOpen.current && !open
    wasOpen.current = open
    if (!closing) return
    const target = returnTo.current
    const id = window.setTimeout(() => {
      const active = document.activeElement
      const lost = !active || active === document.body || !document.contains(active)
      if (lost && target && document.contains(target)) target.focus()
    }, 0)
    return () => window.clearTimeout(id)
  }, [open])

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog
        size="lg"
        className="home-sheet"
        style={{ transitionProperty: 'scale, opacity, translate' }}
      >
        <div className="home-sheet__handle" aria-hidden />
        <div className="home-sheet__head">
          <Dialog.Title className="home-sheet__title">{title}</Dialog.Title>
          <Dialog.Close
            render={(props) => (
              <Button
                {...props}
                variant="ghost"
                shape="square"
                className="home-icon-btn"
                icon={<XIcon size={20} />}
                aria-label={t('common.close')}
              />
            )}
          />
        </div>
        <div className="home-sheet__body">{children}</div>
      </Dialog>
    </Dialog.Root>
  )
}
